<template>
  <hb-basic-page
    :back-button='backButton'
    :extension-height='56'
    :fill-height='loading'
    :loading='loading'
    :title="`${id ? site.name : $t('sites.new_site.new_site')}`"
    no-container-padding
    style='overflow: hidden'
  >
    <template v-slot:app-bar-extension>
      <v-col class='px-0'>
        <v-row align='center' class='flex-nowrap'>
          <!-- Pudotusvalikko välilehdille (vasemmalla) -->
          <v-select
            v-if="id && tabsWithRoute && site"
            :value="currentTabItem"
            :items="tabsWithRoute"
            :menu-props="{ maxHeight: 'none', contentClass: 'tab-select-menu' }"
            item-text="name"
            return-object
            dense
            solo
            hide-details
            background-color="grey darken-2"
            dark
            class="tab-select ml-2"
            @change="onMobileTabSelect"
          >
            <template v-slot:prepend-inner>
              <v-icon small class="mr-1">mdi-menu</v-icon>
            </template>
          </v-select>

          <v-select
            v-else-if="!id && tabsWithoutRoute && site"
            :value="currentTabItem"
            :items="tabsWithoutRoute"
            :menu-props="{ maxHeight: 'none', contentClass: 'tab-select-menu' }"
            item-text="name"
            return-object
            dense
            solo
            hide-details
            background-color="grey darken-2"
            dark
            class="tab-select ml-2"
            @change="onMobileTabSelect"
          >
            <template v-slot:prepend-inner>
              <v-icon small class="mr-1">mdi-menu</v-icon>
            </template>
          </v-select>

          <v-spacer />

          <!-- Toimintopainikkeet (oikealla, vain smAndUp; mobiilissa FAB) -->
          <div v-if='$vuetify.breakpoint.smAndUp' class='mr-2'>
            <v-btn v-if='canSaveSite' :disabled='isSiteDataSame' class='primary'
                   @click='saveSite'>
              <v-icon class='mr-2' left>mdi-content-save</v-icon>
              {{ $t('save') }}
            </v-btn>
            <v-btn v-if='!id && ($isTenantAdmin || $isTenantManager)' :disabled='isSiteDataSame' class='primary'
                   @click='createSite'>
              <v-icon class='mr-2' left>mdi mdi-content-save</v-icon>
              {{ $t('save') }}
            </v-btn>
            <template v-if="tab === 'daybooks' && id && ($isTenantAdmin || $isTenantManager || $isTenantBasic)">
              <v-btn color='primary' class='mr-2' @click='siteDaybook'>
                <v-icon left>mdi-clipboard-outline</v-icon>
                {{ $t('daybook.new') }}
              </v-btn>
              <v-btn v-if='$isTenantAdmin || $isTenantManager' color='primary' @click='openDaybookPrintDialog'>
                <v-icon left>mdi-file-document-outline</v-icon>
                {{ $t('daybook.print_daybook') }}
              </v-btn>
            </template>
          </div>
        </v-row>
      </v-col>
    </template>

    <v-col cols='12' style='margin-bottom: 72px'>
      <v-tabs-items v-model='selectedTab' touchless>
        <!-- PERUSTIEDOT -->
        <v-tab-item value="basic_info" style='height: 100%'>
          <v-container fluid>
            <v-row>
              <v-col
                v-if="tab === 'basic_info'"
                :class="$vuetify.breakpoint.xsOnly ? 'gradient-icons pa-0 ma-0' : ' gradient-icons mt-3'"
                lg='8'
                md='8'
                offset-lg='2'
                offset-md='2'
              >
                <v-card :elevation="$vuetify.breakpoint.xsOnly ? 0 : ''">
                  <v-card-text>
                    <v-row align="center" no-gutters class="mt-4">
                      <v-col>
                        <v-text-field
                          v-model='site.name'
                          :clearable='!$isOnlyTenantBasic && canEditSiteName'
                          :disabled='saving || !canEditSiteName'
                          :hint="!canEditSiteName ? $t('sites.name_locked_after_created') : ''"
                          :label="$t('sites.new_site.work_site_name')"
                          :prefix="site.project ? site.project.code + ' ' : ''"
                          :readonly='$isOnlyTenantBasic'
                          :rules='[$rules.required]'
                          autocomplete='off'
                          class='site-name-field'
                          dense
                          item-value=''
                          light
                          outlined
                          persistent-hint
                          tabindex='1'
                        >
                          <template v-slot:prepend>
                            <v-icon>mdi mdi-office-building</v-icon>
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col cols="auto" class="pb-5 pl-2">
                        <v-btn
                          icon
                          color="primary"
                          :title="$t('common.copy_to_clipboard')"
                          @click="copySiteName"
                        ><v-icon>mdi-content-copy</v-icon></v-btn>
                      </v-col>
                    </v-row>

                    <worker-combobox
                      :label="$t('sites.new_site.lap_person')"
                      :workers="lapWorkers"
                      :value="site.lapWorker"
                      :saving="saving"
                      :disabled="$isOnlyTenantBasic"
                      icon="mdi-account-hard-hat"
                      @change="site.lapWorker = $event"
                    />

                    <v-text-field
                      v-model='site.address'
                      :clearable='!$isOnlyTenantBasic'
                      :disabled='saving'
                      :label="$t('sites.new_site.address')"
                      :readonly='$isOnlyTenantBasic'
                      autocomplete='off'
                      class='mb-5'
                      dense
                      hide-details
                      item-value=''
                      light
                      outlined
                      style='margin-top: -4px'
                      tabindex='2'
                    >
                      <template v-slot:prepend>
                        <v-icon>mdi mdi-map-marker</v-icon>
                      </template>
                      <template v-if='site.address !== null && site.address.length > 0' v-slot:append-outer>
                        <v-btn
                          v-if='!$isOnlyTenantBasic'
                          :fab='$vuetify.breakpoint.xsOnly'
                          :style="{ marginTop: $vuetify.breakpoint.xsOnly ? '-7px' : '-5.5px' }"
                          small
                          text
                          @click='openMapDialog'
                        >
                          <v-icon :left='$vuetify.breakpoint.smAndUp'>mdi mdi-crosshairs-gps
                          </v-icon
                          >
                          <span v-if='$vuetify.breakpoint.smAndUp && !$isOnlyTenantBasic'>{{
                              site.geocoding.address ? $t('sites.show_location') : $t('sites.set_location')
                            }}</span>
                        </v-btn>

                        <v-btn
                          v-if='$isOnlyTenantBasic'
                          :fab='$vuetify.breakpoint.xsOnly'
                          :style="{ marginTop: $vuetify.breakpoint.xsOnly ? '-7px' : '-5.5px' }"
                          small
                          text
                          @click='openMapDialog'
                        >
                          <v-icon :left='$vuetify.breakpoint.smAndUp'>mdi mdi-crosshairs-gps
                          </v-icon
                          >
                          <span v-if='$vuetify.breakpoint.smAndUp && $isOnlyTenantBasic'>{{ $t('sites.show_location')
                            }}</span>
                        </v-btn>
                      </template>
                    </v-text-field>

                    <v-text-field
                      v-model='site.geocoding.address'
                      :label="$t('sites.address_by_coordinates')"
                      autocomplete='off'
                      class='mb-5'
                      dense
                      disabled
                      hide-details
                      item-value=''
                      light
                      outlined
                      tabindex='2'
                    >
                      <template v-slot:prepend>
                        <v-icon>mdi mdi-map-marker</v-icon>
                      </template>
                    </v-text-field>

                    <v-autocomplete
                      v-model='site.project'
                      :clearable='!$isOnlyTenantBasic'
                      :disabled='saving'
                      :item-text="item => [item.name, item.orderer && item.orderer.companyName].filter(Boolean).join(' ')"
                      :items='projects'
                      :label="$t('sites.new_site.project')"
                      :readonly='$isOnlyTenantBasic'
                      autocomplete='off'
                      class='mb-5'
                      dense
                      hide-details
                      item-value='id'
                      light
                      outlined
                      return-object
                      tabindex='3'
                    >
                      <template v-slot:selection='data'>
                        <v-list-item class='ml-n4' style='max-width: 100%'>
                          <v-list-item-content>
                            <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                            <v-list-item-subtitle v-if='data.item.orderer'>{{ data.item.orderer.companyName }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <template v-slot:item='data'>
                        <v-list-item v-bind='data.attr' v-on='data.on'>
                          <v-list-item-content>
                            <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                            <v-list-item-subtitle v-if='data.item.orderer'>{{ data.item.orderer.companyName }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <template v-slot:prepend>
                        <v-icon>mdi mdi-clipboard-account-outline</v-icon>
                      </template>
                    </v-autocomplete>

                    <!-- TYÖMAAMESTARI -->
                    <person-combobox
                      :add-new-text="`${$t('new')} ${$t('sites.new_site.work_site_master')}`"
                      :clearable='!$isOnlyTenantBasic'
                      :label="$t('sites.new_site.work_site_master')"
                      :persons='persons'
                      :readonly='$isOnlyTenantBasic'
                      :saving='saving'
                      :value='site.siteMaster'
                      icon='mdi mdi-account-tie-hat'
                      tabindex='4'
                      @change='v => (site.siteMaster = v)'
                      @add-person="addPerson('siteMaster')"
                    />

                    <!-- TYÖMAAOPAS -->
                    <person-combobox
                      :add-new-text="`${$t('new')} ${$t('sites.new_site.work_site_guide')}`"
                      :clearable='!$isOnlyTenantBasic'
                      :label="$t('sites.new_site.work_site_guide')"
                      :persons='persons'
                      :readonly='$isOnlyTenantBasic'
                      :saving='saving'
                      :value='site.siteGuide'
                      class='mt-n1'
                      icon='mdi mdi-account-tie'
                      tabindex='5'
                      @change='v => (site.siteGuide = v)'
                      @add-person="addPerson('siteGuide')"
                    />

                    <div :class="$vuetify.breakpoint.xsOnly ? 'flex-column-reverse' : 'd-flex flex-row'"
                         style='margin-top: -6px'>
                      <v-col class='pa-0 ma-0' md='8'>
                        <site-view-date-picker
                          v-model='site.plannedStartDate'
                          :label="$t('sites.new_site.planned_start_date')"
                          format='D.M.YYYY'
                          tabindex='5'
                        />
                      </v-col>
                      <v-col :class="$vuetify.breakpoint.smAndUp ? 'pl-4' : ''" class='pa-0 ma-0' md='4'>
                        <v-text-field
                          v-model='site.plannedDuration'
                          :clearable='!$isOnlyTenantBasic'
                          :disabled='saving'
                          :label="$t('sites.new_site.planned_duration_in_days')"
                          :readonly='$isOnlyTenantBasic'
                          autocomplete='off'
                          class='mb-5'
                          dense
                          hide-details
                          inputmode='numeric'
                          item-value=''
                          light
                          outlined
                          tabindex='7'
                          type='number'
                        >
                          <template v-if='$vuetify.breakpoint.xsOnly' v-slot:prepend>
                            <v-icon>mdi mdi-timetable</v-icon>
                          </template>
                        </v-text-field>
                      </v-col>
                    </div>

                    <v-autocomplete
                      v-model='site.workGroup'
                      :disabled='saving || true'
                      :items='workGroups'
                      :label="$t('sites.new_site.no_workgroup_placeholder')"
                      autocomplete='off'
                      class='mb-5'
                      clearable
                      dense
                      hide-details
                      item-text='name'
                      item-value='id'
                      light
                      outlined
                      return-object
                      tabindex='8'
                      @change='onWorkGroupChange'
                    >
                      <template v-slot:prepend>
                        <v-icon>mdi mdi-account-group</v-icon>
                      </template>
                    </v-autocomplete>

                    <v-autocomplete
                      v-model='site.status'
                      :clearable='!$isOnlyTenantBasic'
                      :disabled='saving'
                      :item-text='item => item.desc'
                      :items='statuses'
                      :label="$t('sites.status')"
                      :readonly='$isOnlyTenantBasic'
                      autocomplete='off'
                      class='mb-5'
                      dense
                      hide-details
                      item-value='value'
                      light
                      outlined
                      tabindex='9'
                    >
                      <template v-slot:prepend>
                        <v-icon>mdi mdi-information-outline</v-icon>
                      </template>
                    </v-autocomplete>

                    <equipment-combobox
                      :equipments='equipments'
                      :label="$t('sites.new_site.equipment')"
                      :multiple='true'
                      :readonly='$isOnlyTenantBasic'
                      :saving='saving'
                      :tabindex='9'
                      :value='site.equipments'
                      @change='v => (site.equipments = v)'
                    />

                    <v-text-field
                      v-model='site.billingReference'
                      :clearable='!$isOnlyTenantBasic'
                      :disabled='saving'
                      :label="$t('sites.new_site.reference')"
                      :readonly='$isOnlyTenantBasic'
                      autocomplete='off'
                      class='mb-5'
                      dense
                      hide-details
                      item-value=''
                      light
                      outlined
                      tabindex='11'
                    >
                      <template v-slot:prepend>
                        <v-icon>mdi mdi-numeric</v-icon>
                      </template>
                    </v-text-field>

                    <v-text-field
                      v-model='site.orientationLink'
                      :clearable='!$isOnlyTenantBasic'
                      :disabled='saving'
                      :label="$t('sites.new_site.orientation_link')"
                      :readonly='$isOnlyTenantBasic'
                      autocomplete='off'
                      class='mb-5'
                      dense
                      hide-details
                      item-value=''
                      light
                      outlined
                      tabindex='11'
                    >
                      <template v-slot:prepend>
                        <v-icon>mdi mdi-school-outline</v-icon>
                      </template>
                      <template v-slot:append-outer v-if='site.orientationLink'>
                        <v-btn :style="{ marginTop: $vuetify.breakpoint.xsOnly ? '-7px' : '-5.5px' }" fab text small
                          ><a style='text-decoration: none' :href='orientationLinkHref' target='_blank' rel='noopener' @click.stop>
                            <v-icon>mdi mdi-open-in-new</v-icon>
                          </a></v-btn
                        >
                      </template>
                    </v-text-field>

                    <v-textarea
                      v-model='site.additionalWork'
                      :clearable='!$isOnlyTenantBasic'
                      :disabled='saving'
                      :label="$t('sites.new_site.additional_jobs')"
                      :readonly='$isOnlyTenantBasic'
                      auto-grow
                      autocomplete='off'
                      class='mb-5'
                      dense
                      hide-details
                      item-value=''
                      light
                      outlined
                      tabindex='11'
                    >
                      <template v-slot:prepend>
                        <v-icon>mdi mdi-clipboard-plus-outline</v-icon>
                      </template>
                    </v-textarea>

                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>

        <!-- TARJOUS -->
        <v-tab-item value="quote" style='height: 100%'>
          <v-container fluid>
            <v-row>
              <v-col
                :class="$vuetify.breakpoint.xsOnly ? 'pa-0 ma-0 mt-4' : 'mt-3'"
                cols='12'
                lg='8'
                md='8'
                offset-lg='2'
                offset-md='2'
              >
                <site-quote :site='site' />
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>

        <!-- TYÖTIEDOT -->
        <v-tab-item value="work_info" style='height: 100%'>
          <v-container fluid>
            <v-row>
              <v-col
                :class="$vuetify.breakpoint.xsOnly ? 'gradient-icons pa-0 ma-0' : ' gradient-icons mt-3'"
                lg='8'
                md='8'
                offset-lg='2'
                offset-md='2'
              >
                <template v-for='(work, idx) in sortedWorks'>
                  <v-card
                    :key='work.id || work._key || idx'
                    :class="$vuetify.breakpoint.xsOnly ? 'mb-5 mx-4' : 'mb-4'"
                    :elevation="$vuetify.breakpoint.xsOnly ? 5 : ''"
                  >
                    <v-card-title
                      :class="
                        $vuetify.breakpoint.xsOnly ? 'd-flex justify-space-between pb-0 mb-0' : 'd-flex justify-space-between'
                      "
                      dense
                    >
                      <v-text-field
                        :value='work.title'
                        :readonly='$isOnlyTenantBasic'
                        :disabled='saving'
                        :placeholder="`${$t('sites.new_site.drilling')} ${idx + 1}`"
                        class='pt-0 mt-0'
                        dense
                        hide-details
                        single-line
                        @change='v => changeWorkTitle(work, v)'
                      />
                      <v-menu v-if='work.id && !$isOnlyTenantBasic' offset-y>
                        <template v-slot:activator='{ on, attrs }'>
                          <work-status-chip
                            :status='work.status'
                            class='ml-2'
                            style='cursor: pointer'
                            v-bind='attrs'
                            v-on='on'
                          />
                        </template>
                        <v-list dense>
                          <v-list-item
                            v-for='s in workStatuses'
                            :key='s'
                            :disabled='s === work.status || saving'
                            @click='changeWorkStatus(work, s)'
                          >
                            <v-list-item-title>{{ $t(`sites.work_statuses.${s}`) }}</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                      <work-status-chip v-else :status='work.status' class='ml-2' />
                      <v-btn
                        v-if='!$isOnlyTenantBasic && work.workMethodId'
                        :disabled='saving'
                        icon
                        title='Generoi kuvaus menetelmästä ja ensimmäisestä tiedosta'
                        @click='generateWorkTitle(work)'
                      >
                        <v-icon>mdi mdi-refresh</v-icon>
                      </v-btn>
                      <v-btn v-if='!$isOnlyTenantBasic' icon @click='deleteWork(work)'>
                        <v-icon class='no-gradient' color='error'>delete</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-card-text>
                      <v-row class='mt-5'>
                        <v-col cols='12'>
                          <v-autocomplete
                            :items='workMethods'
                            :label="$t('sites.new_site.method')"
                            :value='work.workMethodId'
                            :readonly='$isOnlyTenantBasic'
                            :disabled='saving'
                            item-text='name'
                            item-value='id'
                            outlined
                            dense
                            hide-details
                            @change='v => changeWorkMethod(work, v)'
                          >
                            <template v-slot:prepend>
                              <v-icon>mdi mdi-shovel</v-icon>
                            </template>
                          </v-autocomplete>
                        </v-col>
                      </v-row>
                      <attribute-form
                        v-if='work.workMethodId'
                        :defs='workScopeDefs(work)'
                        :value='work.attributeValues || []'
                        :disabled='saving || $isOnlyTenantBasic'
                        class='mt-4'
                        @input='v => setWorkAttributeValues(work, v)'
                      />
                    </v-card-text>
                  </v-card>
                </template>
                <v-row class='mb-2'>
                  <v-col class='text-left'>
                    <div
                      :class="$vuetify.breakpoint.xsOnly ? 'py-2 px-4 pt-5' : 'mt-0'"
                      :style="{
                        width: $vuetify.breakpoint.xsOnly ? '100%' : 'auto',
                      }"
                    >
                      <v-btn
                        v-if='!$isOnlyTenantBasic'
                        :style="{ width: $vuetify.breakpoint.xsOnly ? '100%' : 'auto' }"
                        color='primary'
                        @click='addWork'
                      >
                        <v-icon class='no-gradient' left>mdi mdi-plus</v-icon>
                        {{ $t('sites.new_site.add_drilling') }}
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <template v-if='siteAdditionalThings.length > 0'>
                  <v-card
                    v-for='juttu in siteAdditionalThings'
                    :key='`${juttu.id}_${juttu._idx}`'
                    :class="$vuetify.breakpoint.xsOnly ? 'ma-4' : ''"
                    :elevation="$vuetify.breakpoint.xsOnly ? 5 : ''"
                    class='pa-4 mb-4'
                  >
                    <!-- Lisänimikkeen tyyppi on rivin rooli (työmaan lisänimike -ominaisuuden tyypit) -->
                    <site-drilling-multiple-item-select
                      v-if='juttu.itemTypeId'
                      :amount='site.items.find(i => i._idx === juttu._idx).amount'
                      :items='itemsOfType(juttu.itemTypeId)'
                      :itemsite='site.items.find(i => i._idx === juttu._idx).item'
                      :label='typeLabel(juttu.itemTypeId)'
                      :saving='saving'
                      icon='mdi mdi-puzzle-outline'
                      @change='changeDrillingPropertyOnSite($event, juttu._idx)'
                      @delete='deleteDrillingProperty(juttu._idx)'
                    />
                  </v-card>
                </template>

                <v-row class='mb-2'>
                  <v-col class='text-left'>
                    <div
                      :class="$vuetify.breakpoint.xsOnly && 'px-4 pt-2'"
                      :style="{ width: $vuetify.breakpoint.xsOnly ? '100%' : 'auto' }"
                    >
                      <v-menu v-if='!$isOnlyTenantBasic' max-width='200' offset-y top>
                        <template v-slot:activator='{ on, attrs }'>
                          <v-row>
                            <v-col>
                              <v-btn
                                v-if='!$isOnlyTenantBasic'
                                :style="{ width: $vuetify.breakpoint.xsOnly ? '100%' : 'auto' }"
                                color='primary'
                                v-bind='attrs'
                                v-on='on'
                              >
                                <v-icon class='no-gradient' left>mdi mdi-plus</v-icon>
                                {{ $t('sites.add_feature') }}
                              </v-btn>
                            </v-col>
                          </v-row>
                        </template>
                        <v-list>
                          <v-list-item v-for='type in additionalItemTypes' :key='type.id' link @click='addNewDrillingOption(type.id)'>
                            <v-list-item-title>{{ type.name }}</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </div>
                  </v-col>
                </v-row>

                <v-card :elevation="$vuetify.breakpoint.xsOnly ? 0 : ''">
                  <v-card-text>
                    <numeric-input
                      v-model='site.minDrillingLength'
                      :clearable='!$isOnlyTenantBasic'
                      :disabled='saving'
                      :label="$t('sites.new_site.pipe_minimum_invoicing_length')"
                      :readonly='$isOnlyTenantBasic'
                      autocomplete='off'
                      class='mb-5'
                      dense
                      hide-details
                      item-value=''
                      light
                      outlined
                      suffix='m'
                    >
                      <template v-slot:prepend>
                        <v-icon>mdi mdi-arrow-expand-horizontal</v-icon>
                      </template>
                    </numeric-input>

                    <v-textarea
                      v-model='site.additionalInfo'
                      :clearable='!$isOnlyTenantBasic'
                      :disabled='saving'
                      :label="$t('sites.new_site.additional_info')"
                      :readonly='$isOnlyTenantBasic'
                      auto-grow
                      autocomplete='off'
                      class='mb-5'
                      dense
                      hide-details
                      item-text='name'
                      item-value=''
                      light
                      outlined
                    >
                      <template v-slot:prepend>
                        <v-icon>mdi mdi-clipboard-plus-outline</v-icon>
                      </template>
                    </v-textarea>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>

        <!-- TYÖNTEKIJÄT -->
        <v-tab-item value="workers" style='height: 100%'>
          <v-container fluid>
            <v-row>
              <v-col
                :class="$vuetify.breakpoint.xsOnly ? 'gradient-icons pa-0 ma-0 mt-4' : ' gradient-icons mt-3'"
                cols='12'
                lg='8'
                md='8'
                offset-lg='2'
                offset-md='2'
              >
                <v-card :elevation="$vuetify.breakpoint.xsOnly ? 0 : ''">
                  <v-card-text>
                    <v-row>
                      <v-col>
                        <v-autocomplete
                          v-model='site.workGroup'
                          :clearable='!$isOnlyTenantBasic'
                          :disabled='saving'
                          :items='workGroups'
                          :label="$t('sites.new_site.work_group')"
                          :readonly='$isOnlyTenantBasic'
                          autocomplete='off'
                          class='mb-5'
                          dense
                          hide-details
                          item-text='name'
                          item-value='id'
                          light
                          outlined
                          return-object
                          @change='onWorkGroupChange'
                        >
                          <template v-slot:prepend>
                            <v-icon>mdi mdi-account-group</v-icon>
                          </template>
                        </v-autocomplete>
                        <v-autocomplete
                          v-model='selectedWorker'
                          :clearable='!$isOnlyTenantBasic'
                          :disabled='saving'
                          :item-text='item => `${item.person.lastname} ${item.person.firstname}`'
                          :items='filteredWorkers'
                          :label="$tc('sites.workers.add_workers', 1)"
                          :readonly='$isOnlyTenantBasic'
                          autocomplete='off'
                          class='mb-5 mt-4'
                          dense
                          hide-details
                          item-value='id'
                          light
                          outlined
                          return-object
                          @change='onSelectWorker'
                        >
                          <template v-slot:prepend>
                            <v-icon>mdi mdi-account</v-icon>
                          </template>
                        </v-autocomplete>

                        <small v-if='!siteWorkers || (siteWorkers && siteWorkers.length === 0)'>Ei liitettyjä
                          työntekijöitä</small>

                        <v-data-table v-else
                                      :headers='computedHeaders'
                                      :items='siteWorkers'
                                      :items-per-page='20'
                                      class='elevation-0'
                                      hide-default-footer
                                      mobile-breakpoint='800'
                        >
                          <template v-slot:item.firstname='{ item }'>
                            {{ item.firstname }} {{ item.lastname }}
                          </template>
                          <template v-slot:item.workStart='{ item }'>
                            <template v-if='$isOnlyTenantBasic'>
                              {{ $formatDateNoTime(item.workStart) }}
                            </template>
                            <date-picker v-else :date='item.workStart' dense
                                         @change='value => handleWorkerWorkStartChange(value, item)' />
                          </template>
                          <template v-slot:item.workEnd='{ item }'>
                            <template v-if='$isOnlyTenantBasic'>
                              {{ $formatDateNoTime(item.workEnd) }}
                            </template>
                            <date-picker v-else :date='item.workEnd' dense
                                         @change='value => handleWorkerWorkEndChange(value, item)' />
                          </template>
                          <template v-slot:item.intro='{item}'>
                            <template v-if='isWorkerIntroAccepted(item.userId)'>
                              <template v-if='!$vuetify.breakpoint.mobile'>
                                <v-chip color='success' small>{{ $t('sites.intro_checked') }}</v-chip>
                                <span class='ml-2'>{{ isWorkerIntroAccepted(item.userId) }}</span>
                              </template>
                              <template v-else>
                                <span class='mr-2'>{{ isWorkerIntroAccepted(item.userId) }}</span>
                                <v-chip color='success' small>{{ $t('sites.intro_checked') }}</v-chip>
                              </template>
                            </template>
                            <template v-else>
                              <v-chip color='red' dark small>{{ $t('sites.no_intro') }}</v-chip>
                            </template>
                          </template>
                          <template v-slot:item.actions='{item}'>
                            <v-btn v-if="!$isOnlyTenantBasic" fab small text @click='deleteWorker(item.workerId)'>
                              <v-icon class='no-gradient' color='red'>mdi mdi-delete</v-icon>
                            </v-btn>
                          </template>
                        </v-data-table>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <v-btn
                          v-if='!$isOnlyTenantBasic'
                          :block='$vuetify.breakpoint.xsOnly'
                          :loading='loadingSiteWorkerListReport'
                          class='default mr-3'
                          @click='siteWorkerListReport'
                        >
                          <v-icon class='mr-2'>mdi-file-document-outline</v-icon>
                          {{ $t('sites.site_worker_list_report') }}
                        </v-btn>
                        <v-btn
                          v-if='!$isOnlyTenantBasic'
                          :block='$vuetify.breakpoint.xsOnly'
                          :loading='loadingSiteWorkerIntroPDF'
                          class='default mr-3'
                          @click='siteWorkerIntroPDF'
                        >
                          <v-icon class='mr-2'>mdi-file-pdf-box</v-icon>
                          {{ $t('sites.site_introductions_report') }}
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>

        <!-- LIITETIEDOSTOT -->
        <v-tab-item value="attachments" style='height: 100%'>
          <v-container fluid>
            <v-row>
              <v-col
                :class="$vuetify.breakpoint.xsOnly ? 'gradient-icons pa-0 ma-0 mt-4' : ' gradient-icons mt-3'"
                cols='12'
                lg='8'
                md='8'
                offset-lg='2'
                offset-md='2'
              >
                <site-offer-material v-if='site.project && site.project.id' :project-id='site.project.id' />
                <v-card :elevation="$vuetify.breakpoint.xsOnly ? 0 : ''">
                  <v-card-text>
                    <file-upload :compress='false' :objectId='site.id' :use-description='true' object='site' />
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>

        <!-- TYÖMAAPÄIVÄKIRJA -->
        <v-tab-item value="daybooks" class='daybook-list' style='height: 100%'>
          <v-container fluid>
            <v-row>
              <v-col
                v-if='id'
                :class="$vuetify.breakpoint.xsOnly ? 'pa-0 ma-0' : 'mt-3'"
                class='mt-5'
                cols='12'
                lg='10'
                md='10'
                offset-lg='1'
                offset-md='1'
              >
                <!--<v-card elevation="5">
                  <v-card-text>
                    <v-row
                      ><v-col>-->
                <v-select
                  v-if='daybookWorkFilterItems.length > 0'
                  v-model='daybookWorkFilter'
                  :items='daybookWorkFilterItems'
                  :label="$t('daybook.filter_by_work')"
                  class='mb-2'
                  clearable
                  dense
                  hide-details
                  item-text='title'
                  item-value='id'
                  outlined
                  prepend-inner-icon='mdi-clipboard-list-outline'
                >
                  <template v-slot:selection='{ item }'>
                    {{ item.title }}
                    <work-status-chip :status='item.status' x-small class='ml-2' />
                  </template>
                  <template v-slot:item='data'>
                    <v-list-item v-bind='data.attrs' v-on='data.on'>
                      <v-list-item-content>
                        <v-list-item-title class='d-flex align-center'>
                          {{ data.item.title }}
                          <work-status-chip :status='data.item.status' x-small class='ml-2' />
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-select>
                <v-timeline class='pa-4' dense>
                  <v-timeline-item v-for='daybook in filteredDaybooks' :key='daybook.id'
                                   :class="$vuetify.breakpoint.smAndUp && 'ml-n4'">
                    <template v-slot:icon>
                      <v-icon color='white' style='font-size: 1em'>mdi mdi-clipboard-edit-outline</v-icon>
                    </template>
                    <v-card :id="'daybook-' + daybook.id" elevation='5'>
                      <v-card-title>
                        {{ formatDate(daybook.daybookDate) }}
                        <span
                          v-if='daybook.work && daybook.work.title'
                          class='text--secondary ml-3'
                        >
                          {{ daybook.work.title }}
                          <work-status-chip :status='daybook.work.status' x-small class='ml-2' />
                        </span>
                        <v-spacer></v-spacer>
                        <v-btn
                          v-if='$userInfo.id === daybook.userId || $isTenantAdmin || $isTenantManager || $isOnlyTenantBasic'
                          class='text-right gradient-icons'
                          dense
                          fab
                          icon
                          small
                          @click='goToDaybook(daybook.id)'
                        >
                          <v-icon>mdi mdi-clipboard-edit-outline</v-icon>
                        </v-btn>
                        <v-btn
                          v-if='$isTenantAdmin || $isTenantManager'
                          class='text-right gradient-icons'
                          dense fab icon small
                          @click='openDeleteDaybookDialog(daybook)'
                        >
                          <v-icon class='no-gradient' color='red'>mdi mdi-delete</v-icon>
                        </v-btn>
                      </v-card-title>
                      <v-card-text class='gradient-icons pt-2'>
                        <v-row>
                          <v-col>
                            <daybook-entry
                              v-if='daybook.method'
                              :label="$t('daybook.method')"
                              :value='daybook.method.name'
                              icon='mdi mdi-map-legend'
                            />
                            <v-divider
                              v-if='daybook.entryStart || daybook.entryEnd'
                            ></v-divider>
                            <daybook-entry
                              v-if='daybook.entryStart || daybook.entryEnd'
                              :label="$t('daybook.entry_times')"
                              :value="
                                [
                                  [$formatTimeNoDate(daybook.entryStart), $formatTimeNoDate(daybook.entryEnd)].join(' - '),
                                  daybook.containsLunch ? $t('daybook.contains_lunch_long') : '',
                                  `(${$formatHoursAsHHMM(daybook.entryDuration)})`,
                                ]
                                  .filter(Boolean)
                                  .join(' ')
                              "
                              icon='fas fa-clock'
                            />
                            <!-- Sama jako kuin merkintälomakkeella: urakka, lisätyö ja odotus omina ryhminään, kalusto niiden alla. -->
                            <daybook-entry
                              v-if='daybook.timeEntryWorkers && daybook.timeEntryWorkers.length > 0'
                              :label="`${$t('daybook.workers_title')} (${daybook.timeEntryWorkers.length} ${$t('daybook.workers_count')})`"
                              icon='mdi mdi-account-group'
                            >
                              <daybook-time-entries
                                v-for="group in groupTimeEntries(daybook.timeEntryWorkers)"
                                :key="group.chargeType"
                                :charge-type="group.chargeType"
                                :workers="group.workers"
                              />
                            </daybook-entry>
                            <daybook-entry
                              v-if="uniqueEquipments(daybook.timeEntryWorkers).length > 0"
                              :label="$t('daybook.equipments_title')"
                              :value="uniqueEquipments(daybook.timeEntryWorkers).map(eq => formatEquipment(eq)).join('\n')"
                              icon='mdi mdi-excavator'
                            />
                            <v-divider></v-divider>
                            <daybook-entry
                              v-if='daybook.siteSituation'
                              :label="$t('daybook.site_situation')"
                              :value='daybook.siteSituation'
                              icon='mdi mdi-progress-question'
                            />
                            <daybook-entry
                              v-if='daybook.files && daybook.files.length > 0'
                              :label="$t('daybook.images')"
                              :files='daybook.files'
                              icon='mdi mdi-image-multiple'
                            />
                            <daybook-entry
                              v-if="daybook.daybookItems.filter(i => i.type === 'deviation').length > 0"
                              :label="$tc('daybook.deviation', 2)"
                              :value="daybook.daybookItems.filter(i => i.type === 'deviation')"
                              daybookItem
                              icon='mdi mdi-image-broken-variant'
                            />
                            <daybook-entry
                              v-if='daybook.deviationReportMade'
                              :label="$t('daybook.deviation_report_made')"
                              :value="daybook.deviationReportMade ? $t('yes') : ''"
                              icon='mdi mdi-file-delimited-outline'
                            />
                            <daybook-entry
                              v-if='daybook.ordererResponsibilityItems'
                              :label="$t('daybook.orderer_responsibility_items')"
                              :value='daybook.ordererResponsibilityItems'
                              icon='mdi mdi-folder-star-multiple-outline'
                            />
                            <daybook-entry
                              v-if='daybook.reviewsHeld'
                              :label="$t('daybook.reviews_held')"
                              :value='daybook.reviewsHeld'
                              icon='mdi mdi-message-draw'
                            />
                            <v-divider
                              v-if='
                                daybook.diameter ||
                                daybook.drillingLength ||
                                daybook.drillingLengthAtDay ||
                                daybook.pipeLength ||
                                daybook.pipeLengthAtDay ||
                                daybook.ofWhichRock ||
                                daybook.elevationTopEnd ||
                                daybook.elevationLowerEnd
                              '
                            >
                            </v-divider>

                            <template v-if='daybookHasWork(daybook)'>
                              <daybook-entry
                                v-for='attr in daybookAttributes(daybook)'
                                :key='attr.label'
                                :label='attr.label'
                                :value='attr.value'
                                :icon="attr.icon || 'mdi mdi-information-outline'"
                              />
                            </template>
                            <template v-else>
                            <template v-for='(drilling, key) in daybook.daybookDrillings'>
                              <div
                                v-if='
                                  drilling.drillingLengthAtDay !== null ||
                                  drilling.pipeLength !== null ||
                                  drilling.pipeLengthAtDay !== null ||
                                  drilling.ofWhichRock !== null ||
                                  drilling.elevationTopEnd !== null ||
                                  drilling.elevationLowerEnd !== null
                                '
                                :key='drilling.id'
                              >
                                <v-card-title>{{ getWorkTitleForTaskOrder(drilling.taskOrder) || `Poraus ${key + 1}` }}</v-card-title>
                                <daybook-entry
                                  v-if='drilling.method'
                                  :label="$t('sites.new_site.method')"
                                  :value='drilling.method.name'
                                  icon='mdi mdi-shovel'
                                />
                                <daybook-entry
                                  v-if='drilling.diameter'
                                  :label="$t('sites.new_site.pipe_diameter')"
                                  :value='drilling.diameter.name'
                                  icon='mdi mdi-diameter-outline'
                                />
                                <daybook-entry
                                  v-if='getDrillingRearingSize(drilling)'
                                  :label="$t('sites.new_site.rearing_size')"
                                  :value='getDrillingRearingSize(drilling).name'
                                  icon='mdi mdi-diameter-outline'
                                />
                                <daybook-entry
                                  v-if='getDrillingLisatieto(drilling)'
                                  :label="$t('sites.new_site.drilling_additional_info')"
                                  :value='getDrillingLisatieto(drilling)'
                                  icon='mdi mdi-information-outline'
                                />
                                <!--<daybook-entry
                                  v-if="daybook.diameter"
                                  icon="mdi mdi-diameter-outline"
                                  :label="$t('daybook.diameter')"
                                  :value="daybook.diameter.name"
                                />-->
                                <daybook-entry
                                  v-if='drilling.drillingLength'
                                  :label="$t('daybook.drilling_length')"
                                  :value="$formatNumber2DecAppendix(drilling.drillingLength, 'm')"
                                  icon='mdi mdi-arrow-expand-horizontal'
                                />
                                <daybook-entry
                                  v-if='drilling.drillingLengthAtDay'
                                  :label="$t('daybook.drilling_length_at_day')"
                                  :value="$formatNumber2DecAppendix(drilling.drillingLengthAtDay, 'm')"
                                  icon='mdi mdi-arrow-expand-horizontal'
                                />
                                <daybook-entry
                                  v-if='!isNoPipeMethod(drilling.method && drilling.method.name)'
                                  :label="$t('daybook.pipe_length')"
                                  :value="$formatNumber2DecAppendix(drilling.pipeLength, 'm')"
                                  icon='mdi mdi-pipe'
                                />
                                <daybook-entry
                                  v-if='!isNoPipeMethod(drilling.method && drilling.method.name)'
                                  :label="$t('daybook.pipe_length_at_day')"
                                  :value="$formatNumber2DecAppendix(drilling.pipeLengthAtDay, 'm')"
                                  icon='mdi mdi-pipe'
                                />
                                <daybook-entry
                                  v-if='drilling.ofWhichRock'
                                  :label="$t('daybook.of_which_rock')"
                                  :value="$formatNumber2DecAppendix(drilling.ofWhichRock, 'm')"
                                  icon='mdi mdi-image-filter-hdr'
                                />
                                <daybook-entry
                                  v-if='drilling.elevationTopEnd'
                                  :label="$t('daybook.elevation_top_end')"
                                  :value="$formatNumber2DecAppendix(drilling.elevationTopEnd, 'mm')"
                                  icon='mdi mdi-elevation-rise'
                                />
                                <daybook-entry
                                  v-if='drilling.elevationLowerEnd'
                                  :label="$t('daybook.elevation_lower_end')"
                                  :value="$formatNumber2DecAppendix(drilling.elevationLowerEnd, 'mm')"
                                  icon='mdi mdi-elevation-decline'
                                />
                                <v-divider></v-divider>
                              </div>
                            </template>
                            </template>

                            <daybook-entry
                              v-if="daybook.daybookItems.filter(i => i.type === 'bought').length > 0"
                              :label="$tc('daybook.bought', 2)"
                              :value="daybook.daybookItems.filter(i => i.type === 'bought')"
                              daybookItem
                              icon='mdi mdi-cart-arrow-down'
                            />
                            <daybook-entry
                              v-if="daybook.daybookItems.filter(i => i.type === 'ordered').length > 0"
                              :label="$tc('daybook.ordered', 2)"
                              :value="daybook.daybookItems.filter(i => i.type === 'ordered')"
                              daybookItem
                              icon='mdi mdi-shopping-outline'
                            />
                            <daybook-entry
                              v-if='daybook.ordererNotices'
                              :label="$t('daybook.orderer_notices')"
                              :value='daybook.ordererNotices'
                              icon='mdi mdi-message-alert'
                            />
                            <daybook-entry
                              v-if='daybook.other'
                              :label="$t('daybook.other')"
                              :value='daybook.other'
                              icon='mdi mdi-frequently-asked-questions'
                            />
                            <daybook-entry
                              v-if='daybook.user'
                              :label="$t('daybook.user')"
                              :value="[daybook.user.lastName, daybook.user.firstName].join(' ')"
                              icon='mdi mdi-account'
                            />
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-timeline-item>
                </v-timeline>
              </v-col>
              <v-col v-else>
                <no-content-view
                  :description="$t('sites.not_possible_to_add_daybook_description')"
                  :reloadButton='false'
                  :title="$t('sites.not_possible_to_add_daybook_title')"
                  title-icon='mdi mdi-card-bulleted-off'
                />
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>

        <!-- PEREHDYTYKSET -->
        <v-tab-item value="introduction" style='height: 100%'>
          <v-container fluid>
            <v-row>
              <v-col
                :class="$vuetify.breakpoint.xsOnly ? 'pa-0 ma-0 mt-4' : 'mt-3'"
                cols='12'
                lg='8'
                md='8'
                offset-lg='2'
                offset-md='2'
              >
                <site-introduction ref="siteIntroduction" :site='site' />
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>

        <!-- TARKASTUKSET -->
        <v-tab-item value="equipmentchecks" style='height: 100%'>
          <v-container fluid>
            <v-row>
              <v-col
                :class="$vuetify.breakpoint.xsOnly ? 'pa-0 ma-0 mt-4' : 'mt-3'"
                cols='12'
                lg='8'
                md='8'
                offset-lg='2'
                offset-md='2'
              >
                <site-equipment-checks :site='site' />
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>

        <!-- MVR-MITTARIT -->
        <v-tab-item value="mvr" style='height: 100%'>
          <v-container fluid>
            <v-row>
              <v-col
                :class="$vuetify.breakpoint.xsOnly ? 'pa-0 ma-0 mt-4' : 'mt-3'"
                cols='12'
                lg='8'
                md='8'
                offset-lg='2'
                offset-md='2'
              >
                <site-mvr-checks :site='site' />
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>

        <!-- MATERIAALIT -->
        <v-tab-item value="materials" style='height: 100%'>
          <v-container fluid>
            <v-row>
              <v-col
                :class="$vuetify.breakpoint.xsOnly ? 'pa-0 ma-0 mt-4' : 'mt-3'"
                cols='12'
                lg='10'
                md='10'
                offset-lg='1'
                offset-md='1'
              >
                <site-materials ref='siteMaterials' :site='site' />
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>

        <!-- OSTOLASKUT (vain tenant-admin) -->
        <v-tab-item v-if="$isTenantAdmin" value="purchase_invoices" style='height: 100%'>
          <v-container fluid>
            <v-row>
              <v-col
                :class="$vuetify.breakpoint.xsOnly ? 'pa-0 ma-0 mt-4' : 'mt-3'"
                cols='12'
                lg='10'
                md='10'
                offset-lg='1'
                offset-md='1'
              >
                <site-netvisor-invoices :site='site' />
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>

        <!-- MYYNTILASKUT (vain tenant-admin) -->
        <v-tab-item v-if="$isTenantAdmin" value="sales_invoices" style='height: 100%'>
          <v-container fluid>
            <v-row>
              <v-col
                :class="$vuetify.breakpoint.xsOnly ? 'pa-0 ma-0 mt-4' : 'mt-3'"
                cols='12'
                lg='10'
                md='10'
                offset-lg='1'
                offset-md='1'
              >
                <site-netvisor-sales-invoices :site='site' />
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>

        <!-- LASKUTUSEHDOTUS (vain tenant-admin) -->
        <v-tab-item v-if="$isTenantAdmin" value="billing_proposal" style='height: 100%'>
          <v-container fluid>
            <v-row>
              <v-col
                :class="$vuetify.breakpoint.xsOnly ? 'pa-0 ma-0 mt-4' : 'mt-3'"
                cols='12'
                lg='10'
                md='10'
                offset-lg='1'
                offset-md='1'
              >
                <site-billing-proposal :site='site' />
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>
      </v-tabs-items>

      <div v-if='$vuetify.breakpoint.xsOnly'>
        <v-btn
          v-if="id && tab === 'daybooks' && ($isTenantAdmin || $isTenantManager || $isTenantBasic)"
          @click='siteDaybook'
          class="mr-3"
          color="primary"
          fab
          bottom
          right
          fixed
        >
          <v-icon class="no-gradient ma-0" left>mdi-plus</v-icon>
        </v-btn>
        <v-btn
          v-if="id && tab === 'materials'"
          @click='newDispatchNote'
          class="mr-3"
          color="primary"
          fab
          bottom
          right
          fixed
        >
          <v-icon class="no-gradient ma-0" left>mdi-truck-delivery</v-icon>
        </v-btn>
        <v-btn
          v-if='canSaveSite'
          :disabled='isSiteDataSame'
          bottom
          color='primary'
          fab
          fixed
          right
          @click='saveSite'
        >
          <v-icon>mdi mdi-content-save</v-icon>
        </v-btn>

        <v-btn
          v-if='!id && ($isTenantAdmin || $isTenantManager)'
          :disabled='isSiteDataSame'
          bottom
          class='primary'
          fab
          fixed
          right
          @click='createSite'
        >
          <v-icon>mdi mdi-content-save</v-icon>
        </v-btn>
      </div>
      <add-person-dialog ref='AddPersonDialog' @master-created='siteMasterCreated' @guide-created='siteGuideCreated' />
      <map-dialog ref='MapDialog' :geocoding='this.site.geocoding' @change='setLocation' />
    </v-col>

    <v-dialog v-model="daybookPrintDialog" max-width="400">
      <v-card>
        <v-card-title>{{ $t('daybook.print_daybook') }}</v-card-title>
        <v-card-text>
          <v-menu v-model="printStartDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="formatIsoDateFi(printStartDate)"
                :label="$t('daybook.print_start_date')"
                prepend-icon="mdi-calendar"
                readonly v-bind="attrs" v-on="on" outlined dense
              />
            </template>
            <v-date-picker v-model="printStartDate" locale="fi" @input="printStartDateMenu = false" />
          </v-menu>
          <v-menu v-model="printEndDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="formatIsoDateFi(printEndDate)"
                :label="$t('daybook.print_end_date')"
                prepend-icon="mdi-calendar"
                readonly v-bind="attrs" v-on="on" outlined dense
              />
            </template>
            <v-date-picker v-model="printEndDate" locale="fi" @input="printEndDateMenu = false" />
          </v-menu>
          <v-select
            v-if="daybookWorkFilterItems.length > 0"
            v-model="printWorkIds"
            :items="daybookWorkFilterItems"
            :label="$t('daybook.print_works')"
            chips
            clearable
            dense
            item-text="title"
            item-value="id"
            multiple
            outlined
            prepend-icon="mdi-clipboard-list-outline"
            small-chips
          >
            <template v-slot:item="data">
              <v-list-item v-bind="data.attrs" v-on="data.on">
                <v-list-item-action>
                  <v-checkbox :input-value="data.attrs.inputValue" />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title class="d-flex align-center">
                    {{ data.item.title }}
                    <work-status-chip :status="data.item.status" x-small class="ml-2" />
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="daybookPrintDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary" :loading="loadingDaybookPrint" :disabled="!printStartDate || !printEndDate" @click="printDaybooks">
            <v-icon left>mdi-file-document-outline</v-icon>
            {{ $t('daybook.print_daybook') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </hb-basic-page>
</template>

<script>
import siteApi from '@/api/site';
import geocodingApi from '@/api/geocoding';
import itemApi from '@/api/item';
import fileApi from '@/api/file';
import personApi from '@/api/person';
import workGroupApi from '@/api/workGroup';
import equipmentApi from '@/api/equipment';
import projectApi from '@/api/project';
import workerApi from '@/api/worker';
import reportApi from '@/api/report';
import introductionApi from '@/api/introduction';
import downloadjs from 'downloadjs';
import SiteViewDatePicker from '@/components/Sites/SiteViewDatePicker';
import AddPersonDialog from '@/components/AddPersonDialog';
import PersonCombobox from '@/components/PersonCombobox';
import WorkerCombobox from '@/components/WorkerCombobox';
import uniqBy from 'lodash/uniqBy';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import EquipmentCombobox from '@/components/EquipmentCombobox';
import dayjs from 'dayjs';
import DatePicker from '@/components/DatePicker';
import daybookApi from '@/api/daybook';
import DaybookEntry from '@/components/Sites/DaybookEntry';
import DaybookTimeEntries from '@/components/SiteDaybook/DaybookTimeEntries';
import { equipmentName, groupTimeEntries, uniqueEquipments } from '@/helpers/daybookTimeEntries';
import MapDialog from '@/components/MapDialog';
import NoContentView from '@/components/NoContentView';
import NumericInput from '@/components/NumericInput';
import SiteDrillingMultipleItemSelect from '@/components/Sites/SiteDrillingMultipleItemSelect';
import FileUpload from '@/components/FileUpload';
import orderBy from 'lodash.orderby';
import SiteIntroduction from '@/components/Sites/SiteIntroduction';
import axios from 'axios';
import SiteEquipmentChecks from '@/components/Sites/SiteEquipmentChecks';
import SiteMvrChecks from '@/components/Sites/SiteMvrChecks.vue';
import SiteMaterials from '@/components/Sites/SiteMaterials';
import SiteQuote from '@/components/Sites/SiteQuote';
import SiteNetvisorInvoices from '@/components/Sites/SiteNetvisorInvoices';
import SiteNetvisorSalesInvoices from '@/components/Sites/SiteNetvisorSalesInvoices';
import SiteBillingProposal from '@/components/Sites/SiteBillingProposal';
import SiteOfferMaterial from '@/components/Sites/SiteOfferMaterial';
import AttributeForm from '@/components/Work/AttributeForm';
import WorkStatusChip from '@/components/Work/WorkStatusChip';
import workMethodsApi from '@/api/workMethods';
import { activeTypeIds, allItemTypes, hasPurpose, isNoPipeMethod, isPipeType, loadItemTypes, PURPOSE, typeName } from '@/helpers/itemTypes';

export default {
  name: 'SiteView',
  components: {
    SiteEquipmentChecks,
    SiteIntroduction,
    FileUpload,
    SiteDrillingMultipleItemSelect,
    NumericInput,
    NoContentView,
    MapDialog,
    DaybookEntry,
    DaybookTimeEntries,
    DatePicker,
    EquipmentCombobox,
    PersonCombobox,
    WorkerCombobox,
    AddPersonDialog,
    SiteViewDatePicker,
    SiteMvrChecks,
    SiteMaterials,
    SiteQuote,
    SiteNetvisorInvoices,
    SiteNetvisorSalesInvoices,
    SiteBillingProposal,
    SiteOfferMaterial,
    AttributeForm,
    WorkStatusChip,
  },
  props: {
    tab: {
      type: String,
      default: 'basic_info',
    },
    id: {
      type: [Number, String],
    },
  },
  data() {
    return {
      workMethods: [],
      tabsWithRoute: [
        {
          name: this.$t('sites.new_site.work_site_basic_info'),
          route: {
            name: 'tenant_site_edit_site',
            params: { id: this.id, tab: 'basic_info' },
          },
        },
        {
          name: this.$t('sites.quote.title'),
          route: {
            name: 'tenant_site_edit_site',
            params: { id: this.id, tab: 'quote' },
          },
        },
        {
          name: this.$t('sites.new_site.work_site_drilling_info'),
          route: {
            name: 'tenant_site_edit_site',
            params: { id: this.id, tab: 'work_info' },
          },
        },
        {
          name: this.$tc('sites.workers.title', 2),
          route: {
            name: 'tenant_site_edit_site',
            params: { id: this.id, tab: 'workers' },
          },
        },
        {
          name: this.$tc('sites.attachment', 2),
          route: {
            name: 'tenant_site_edit_site',
            params: { id: this.id, tab: 'attachments' },
          },
        },
        {
          name: this.$tc('daybook.title', 2),
          route: {
            name: 'tenant_site_edit_site',
            params: { id: this.id, tab: 'daybooks' },
          },
        },
        {
          name: this.$t('sites.introduction.title'),
          route: {
            name: 'tenant_site_edit_site',
            params: { id: this.id, tab: 'introduction' },
          },
        },
        {
          name: this.$t('sites.equipment_checks.title'),
          route: {
            name: 'tenant_site_edit_site',
            params: { id: this.id, tab: 'equipmentchecks' },
          },
        },
        {
          name: this.$t('sites.mvr.title'),
          route: {
            name: 'tenant_site_edit_site',
            params: { id: this.id, tab: 'mvr' },
          },
        },
        {
          name: this.$t('inventory.materials'),
          route: {
            name: 'tenant_site_edit_site',
            params: { id: this.id, tab: 'materials' },
          },
        },
      ],
      tabsWithoutRoute: [
        {
          name: this.$t('sites.new_site.work_site_basic_info'),
          tab: 'basic_info',
        },
        {
          name: this.$t('sites.new_site.work_site_drilling_info'),
          tab: 'work_info',
        },
        {
          name: this.$tc('sites.workers.title', 2),
          tab: 'workers',
        },
        {
          name: this.$tc('sites.attachment', 2),
          tab: 'attachments',
        },
        {
          name: this.$tc('daybook.title', 2),
          tab: 'daybooks',
        },
      ],
      statuses: [
        { desc: this.$t('sites.statuses.CREATED'), value: 'CREATED' },
        { desc: this.$t('sites.statuses.SCHEDULED'), value: 'SCHEDULED' },
        { desc: this.$t('sites.statuses.INPROGRESS'), value: 'INPROGRESS' },
        { desc: this.$t('sites.statuses.DONE'), value: 'DONE' },

        { desc: this.$t('sites.statuses.BILLED'), value: 'BILLED' },
        { desc: this.$t('sites.statuses.DELETED'), value: 'DELETED' },
      ],
      workStatuses: ['NOTSTARTED', 'INPROGRESS', 'DONE'],
      files: [],
      loading: true,
      searchPhrase: '',
      loadingSiteWorkerListReport: false,
      daybookPrintDialog: false,
      daybookWorkFilter: null,
      printWorkIds: [],
      printStartDate: null,
      printStartDateMenu: false,
      printEndDate: null,
      printEndDateMenu: false,
      loadingDaybookPrint: false,
      selectedTab: 'basic_info',
      saving: false,
      items: [],
      persons: [],
      workGroups: [],
      equipments: [],
      projects: [],
      workers: [],
      daybooks: [],
      introductions: [],
      loadingSiteWorkerIntroPDF: false,
      site: {
        name: '',
        address: '',
        project: null,
        siteMaster: null,
        siteGuide: null,
        plannedStartDate: null,
        plannedDuration: '',
        workGroup: null,
        equipments: [],
        additionalWork: '',
        billingReference: '',
        orientationLink: '',
        diameters: [],
        drillingLength: '',
        minDrillingLength: '',
        centeringRings: [],
        methods: [],
        additionalInfo: '',
        quote: '',
        hidden: false,
        status: 'CREATED',
        workers: [],
        items: [],
        works: [],
        geocoding: {
          result: null,
          lat: null,
          lng: null,
          id: null,
          address: '',
        },
      },
      selectedWorker: null,
      siteWhenMounted: {},
      headers: [
        {
          text: `${this.$t('sites.workers.name')}`,
          align: 'start',
          filterable: true,
          value: 'firstname',
        },
        { text: `${this.$t('sites.working_starts')}`, sortable: false, value: 'workStart' },
        { text: `${this.$t('sites.working_ends')}`, sortable: false, value: 'workEnd' },
        { text: `${this.$t('sites.introduction_completed')}`, sortable: false, class: 'admin-only', value: 'intro' },
        { text: ``, align: 'end', sortable: false, class: 'admin-only', value: 'actions' },
      ],
    };
  },
  async mounted() {
    this.loading = true;

    // Osto-, myyntilasku- ja Laskutusehdotus-välilehdet vain tenant-adminille (Netvisor-taloustieto).
    if (this.id && this.$isTenantAdmin) {
      this.tabsWithRoute.push({
        name: this.$t('sites.purchase_invoices.title'),
        route: {
          name: 'tenant_site_edit_site',
          params: { id: this.id, tab: 'purchase_invoices' },
        },
      });
      this.tabsWithRoute.push({
        name: this.$t('sites.sales_invoices.title'),
        route: {
          name: 'tenant_site_edit_site',
          params: { id: this.id, tab: 'sales_invoices' },
        },
      });
      this.tabsWithRoute.push({
        name: this.$t('sites.billing_proposal.title'),
        route: {
          name: 'tenant_site_edit_site',
          params: { id: this.id, tab: 'billing_proposal' },
        },
      });
    }

    if (this.id) {
      this.site.id = parseInt(this.id);
      await this.getSite();
      this.restoreDaybookWorkFilter();
    } else {
      if (!this.site.works || this.site.works.length === 0) {
        this.addWork();
      }
    }
    this.selectedTab = this.tab;
    await Promise.all([
      (async () => {
        await this.getItems();
      })(),
      (async () => {
        await this.getPersons();
      })(),
      (async () => {
        await this.getWorkGroups();
      })(),
      (async () => {
        await this.getEquipments();
      })(),
      (async () => {
        await this.getProjects();
      })(),
      (async () => {
        await this.getWorkers();
      })(),
      (async () => {
        await this.getDaybooks();
      })(),
      (async () => {
        await this.getIntroductionsBySite();
      })(),
    ]);

    this.loading = false;

    if (this.$route.hash) {
      await this.$nextTick();
      setTimeout(() => {
        const el = document.querySelector(this.$route.hash);
        if (el) {
          let container = el.parentElement;
          while (container && container !== document.documentElement) {
            const { overflow, overflowY } = window.getComputedStyle(container);
            if (/auto|scroll/.test(overflow) || /auto|scroll/.test(overflowY)) break;
            container = container.parentElement;
          }
          const isWindow = !container || container === document.documentElement;
          const scrollTop = isWindow ? window.scrollY : container.scrollTop;
          const containerTop = isWindow ? 0 : container.getBoundingClientRect().top;
          const top = el.getBoundingClientRect().top - containerTop + scrollTop - 180;
          (isWindow ? window : container).scrollTo({ top });
        }
      }, 200);
    }
  },
  computed: {
    orientationLinkHref() {
      const v = (this.site.orientationLink || '').trim();
      if (!v) return '';
      return /^https?:\/\//i.test(v) ? v : `https://${v}`;
    },
    lapWorkers() {
      return this.workers.filter(w => w.userId);
    },
    computedHeaders() {
      if (this.$isOnlyTenantBasic) {
        return this.headers.filter(h => h.class !== 'admin-only');
      }
      return this.headers;
    },
    canEditSiteName() {
      // Site name can only be edited when status is CREATED
      return this.site && this.site.status === 'CREATED';
    },
    sortedWorks() {
      return [...(this.site.works || [])].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    },
    // Lisää ominaisuus -valikon tyypit
    additionalItemTypes() {
      return allItemTypes().filter(type => type.active && type.siteAdditionalItem);
    },
    siteAdditionalThings() {
      return this.site?.items?.filter(i => i.task === 'additional_item') || [];
    },
    daybookWorkFilterItems() {
      return [...(this.site?.works || [])].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    },
    filteredDaybooks() {
      if (!this.daybookWorkFilter) return this.daybooks;
      return this.daybooks.filter(d => d.work && d.work.id === this.daybookWorkFilter);
    },
    siteDrillings() {
      const d = this.site?.items?.filter(i => i.task === 'drilling') || [];

      const tasks = Array.from(new Set(d.map(i => i.taskOrder)));
      const res = {};
      tasks.forEach(t => {
        res[t] = d.filter(r => r.taskOrder === t);
      });
      const keys = Object.keys(res);
      keys.sort((a, b) => parseInt(a) - parseInt(b));
      const res1 = [];
      keys.forEach(k => {
        res1.push({
          pipe: res[k].find(r => isPipeType(r.itemTypeId)),
          method: res[k].find(r => hasPurpose(r.itemTypeId, PURPOSE.DRILLING_METHOD)),
          rearingSize: res[k].find(r => hasPurpose(r.itemTypeId, PURPOSE.REAMING_SIZE)),
          lisatieto: (res[k].find(r => hasPurpose(r.itemTypeId, PURPOSE.DRILLING_METHOD)) || {}).lisatieto || null,
          task: 'drilling',
          taskOrder: parseInt(k),
        });
      });

      return res1;
    },
    filteredWorkers() {
      const ids = {};
      this.siteWorkers?.forEach(w => (ids[w.workerId] = true));
      const workers = this.workers.filter(w => !ids[w.workerId] && w.person?.email?.indexOf('hubble.fi') === -1);
      workers.sort((a, b) => `${a.person.lastname} ${a.person.firstname}`.localeCompare(`${b.person.lastname} ${b.person.firstname}`));
      return workers;
    },
    siteWorkers() {
      if (!this.site.workers) return [];
      const workers = uniqBy([...this.site.workers], 'workerId');
      workers.sort((a, b) => `${a.lastname} ${a.firstname}`.localeCompare(`${b.lastname} ${b.firstname}`));
      return workers;
    },
    isSiteDataSame() {
      return isEqual(this.siteWhenMounted, this.site);
    },
    canSaveSite() {
      const editingTabs = ['basic_info', 'quote', 'work_info', 'workers'];
      return this.id && (this.$isTenantAdmin || this.$isTenantManager) && editingTabs.includes(this.tab);
    },
    currentTabItem() {
      if (this.id) {
        return this.tabsWithRoute.find(t => t.route.params.tab === this.tab) || this.tabsWithRoute[0];
      } else {
        return this.tabsWithoutRoute.find(t => t.tab === this.selectedTab) || this.tabsWithoutRoute[0];
      }
    },
    backButton() {
      // Ali­välilehdellä takaisin-nuoli vie työmaan etusivulle (Perustiedot).
      if (this.id && this.tab !== 'basic_info') {
        return {
          show: true,
          to: { name: 'tenant_site_edit_site', params: { id: this.id, tab: 'basic_info' } },
        };
      }
      // Etusivulla (basic_info) tai uutta työmaata luotaessa: takaisin listaukseen / edelliselle.
      return { show: true, fallback: 'tenant_worksites' };
    },
  },
  watch: {
    selectedTab(val) {
      if (this.id && val !== this.tab) {
        this.$router.replace({ name: 'tenant_site_edit_site', params: { id: this.site.id, tab: val } });
      }
    },
    tab(val) {
      if (this.id && val !== this.selectedTab) {
        this.selectedTab = val;
      }
      // Reload data when tab changes
      this.onTabChange(val);
    },
    daybookWorkFilter(val) {
      if (!this.site.id) return;
      const key = 'daybookWorkFilter_' + this.site.id;
      val ? localStorage.setItem(key, val) : localStorage.removeItem(key);
    },
  },
  methods: {
    // Palauttaa työmaakohtaisen päiväkirjasuodattimen, jos tallennettu työ on yhä olemassa.
    restoreDaybookWorkFilter() {
      const stored = localStorage.getItem('daybookWorkFilter_' + this.site.id);
      const workId = stored ? parseInt(stored) : null;
      if (workId && (this.site.works || []).some(w => w.id === workId)) {
        this.daybookWorkFilter = workId;
      }
    },
    // Työ = work_method + attribuuttiarvot (uusi malli).
    workScopeDefs(work) {
      const method = this.workMethods.find(m => m.id === work.workMethodId);
      return (method && method.attributes ? method.attributes : []).filter(d => d.scope === 'WORK');
    },
    addWork() {
      const orders = (this.site.works || []).map(w => w.sortOrder || 0);
      const maxOrder = Math.max(0, ...orders);
      const works = [...(this.site.works || [])];
      works.push({
        _key: this.$randomString(),
        title: `${this.$t('sites.new_site.drilling')} ${works.length + 1}`,
        sortOrder: maxOrder + 10,
        workMethodId: null,
        attributeValues: [],
        status: 'NOTSTARTED',
      });
      this.site.works = works;
    },
    deleteWork(work) {
      this.$confirmDelete('Poista työ').then(confirm => {
        if (confirm) {
          this.site.works = (this.site.works || []).filter(w => w !== work);
        }
      });
    },
    changeWorkTitle(work, value) {
      this.$set(work, 'title', value);
    },
    // Tila tallennetaan heti omalla rajapinnalla; myös siteWhenMounted päivitetään,
    // jotta tilan vaihto ei merkitse lomaketta muokatuksi.
    async changeWorkStatus(work, status) {
      try {
        const updated = await siteApi.updateSiteWorkStatus(this.id, work.id, status);
        this.$set(work, 'status', updated.status);
        const mounted = ((this.siteWhenMounted && this.siteWhenMounted.works) || []).find(w => w.id === work.id);
        if (mounted) this.$set(mounted, 'status', updated.status);
        this.$showSuccessNotification(this.$t('sites.work_status_changed'));
      } catch (e) {
        console.log(e);
        this.$showErrorNotification(this.$t('sites.status_change_failed'));
      }
    },
    // Generoi työn kuvaus: menetelmän nimi + ensimmäinen täytetty WORK-attribuutti (näyttöjärjestyksessä),
    // välilyönnillä erotettuna. ITEMREF → nimikkeen nimi, teksti/numero sellaisenaan.
    generateWorkTitle(work) {
      const method = this.workMethods.find(m => m.id === work.workMethodId);
      const parts = [];
      if (method && method.name) {
        parts.push(method.name);
      }
      const defs = this.workScopeDefs(work)
        .slice()
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
      const values = work.attributeValues || [];
      for (const d of defs) {
        const v = values.find(x => x.defId === d.id);
        const formatted = v ? this.formatAttrValue(v, d) : null;
        if (formatted != null && formatted !== '') {
          parts.push(formatted);
          break;
        }
      }
      this.$set(work, 'title', parts.join(' '));
    },
    changeWorkMethod(work, methodId) {
      this.$set(work, 'workMethodId', methodId);
      // Vaihdettaessa menetelmää vanhat attribuuttiarvot kuuluvat eri määrittelyihin → tyhjennetään.
      this.$set(work, 'attributeValues', []);
    },
    setWorkAttributeValues(work, values) {
      this.$set(work, 'attributeValues', values);
    },
    copySiteName() {
      const code = this.site.project ? this.site.project.code : null;
      const name = this.site.name || '';
      const text = (code ? code + ' ' + name : name).trim();
      navigator.clipboard.writeText(text);
      this.$showSuccessNotification(this.$t('common.copy_to_clipboard_success'));
    },
    isNoPipeMethod(methodName) {
      return isNoPipeMethod(methodName);
    },
    itemsOfType(typeId) {
      return this.items.filter(item => item.typeId === typeId);
    },
    typeLabel(typeId) {
      return typeName(typeId);
    },
    getDrillingRearingSize(drilling) {
      if (drilling.rearingSize) return drilling.rearingSize;
      const siteDrilling = this.siteDrillings.find(d => d.taskOrder === drilling.taskOrder);
      return siteDrilling?.rearingSize?.item || null;
    },
    getDrillingLisatieto(drilling) {
      const siteDrilling = this.siteDrillings.find(d => d.taskOrder === drilling.taskOrder);
      return siteDrilling?.lisatieto || null;
    },
    groupTimeEntries,
    uniqueEquipments,
    formatEquipment(eq) {
      const base = equipmentName(eq);
      return eq.licenseNumber ? `${base} (${eq.licenseNumber})` : base;
    },
    onMobileTabSelect(item) {
      if (this.id && item.route) {
        this.$router.replace(item.route);
      } else if (item.tab) {
        this.selectedTab = item.tab;
      }
    },
    async siteWorkerIntroPDF() {
      if (!this.site || !this.site.id) return false;
      this.loadingSiteWorkerIntroPDF = true;
      let data;
      try {
        data = await axios({
          url: 'api/tenant/report/introduction/' + this.id + '/data',
          method: 'GET',
        }).then(r => r.data);
      } catch (e) {
        console.warn('Työmaan tietojen haku epäonnistui', e.Message);
      }
      if (!data) return;
      try {
        const filename = this.$fileNameSanitizer(`${this.site.name}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}_perehdytykset.pdf`);
        downloadjs(await reportApi.getSiteWorkerIntroReport(this.site.id), filename, 'application/pdf');
      } catch (e) {
        console.log('PDF:n haku epäonnistui', e);
      }
      this.loadingSiteWorkerIntroPDF = false;
    },
    getWorkerIntroduction(workerId) {
      const introductionData = this.introductions.find((intro) => intro.userId === workerId);
      return introductionData;
    },
    isWorkerIntroAccepted(workerId) {
      const intro = this.getWorkerIntroduction(workerId);
      if (typeof intro === 'undefined') return false;
      return this.$formatDateNoTime(dayjs(intro.accepted));
    },
    changeDrillingPropertyOnSite(event, _idx) {
      let items = [...this.site.items];
      items.forEach(i => {
        if (i._idx === _idx) {
          if (typeof event === 'object') {
            i.item = event;
          } else if (parseInt(event)) {
            i.amount = event;
          }
        }
      });
      this.site.items = items;
    },
    getDrillingTitle(drilling) {
      const work = (this.site.works || []).find(w => w.sortOrder === drilling.taskOrder);
      return work ? work.title : null;
    },
    getWorkTitleForTaskOrder(taskOrder) {
      const work = (this.site.works || []).find(w => w.sortOrder === taskOrder);
      return work && work.title ? work.title : null;
    },
    // Aikajananäkymä uudelle, työkohtaiselle päiväkirjamerkinnälle.
    daybookHasWork(daybook) {
      return !!(daybook.work && daybook.work.id);
    },
    daybookAttributes(daybook) {
      const methodId = daybook.work && daybook.work.workMethodId;
      const method = this.workMethods.find(m => m.id === methodId);
      const defs = (method && method.attributes ? method.attributes : [])
        .filter(d => d.scope === 'DAY')
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
      const values = daybook.attributeValues || [];
      return defs
        .map(d => {
          const v = values.find(x => x.defId === d.id);
          if (!v) return null;
          const val = this.formatAttrValue(v, d);
          return val == null ? null : { label: d.label, value: d.unit ? `${val} ${d.unit}` : val, icon: d.icon };
        })
        .filter(Boolean);
    },
    formatAttrValue(v, def) {
      switch (def.valueType) {
        case 'NUMBER':
          return v.valueNumber != null ? String(v.valueNumber) : null;
        case 'BOOLEAN':
          return v.valueBoolean == null ? null : v.valueBoolean ? 'Kyllä' : 'Ei';
        case 'ITEMREF':
          return v.valueItem ? v.valueItem.name : null;
        case 'DATE':
          return v.valueDate || null;
        default:
          return v.valueText || null;
      }
    },
    changeDrillingTitle(drilling, value) {
      const works = [...(this.site.works || [])];
      const idx = works.findIndex(w => w.sortOrder === drilling.taskOrder);
      if (idx > -1) {
        this.$set(works, idx, { ...works[idx], title: value });
      } else {
        works.push({ title: value, sortOrder: drilling.taskOrder });
      }
      this.site.works = works;
    },
    addNewDrillingOption(itemTypeId) {
      if (this.site) {
        let items = [...this.site.items];
        items.push({
          item: this.itemsOfType(itemTypeId)[0] || null,
          taskOrder: 0,
          task: 'additional_item',
          itemTypeId,
          _idx: this.$randomString(),
          amount: 1,
        });
        this.site.items = items;
      }
    },
    deleteDrillingProperty(_idx) {
      const iIdx = this.site?.items.findIndex(i => i.task === 'additional_item' && i._idx === _idx);
      if (iIdx > -1) {
        const items = [...this.site.items];
        items.splice(iIdx, 1);
        this.site.items = items;
      }
    },
    confirmDrillingDelete(drilling) {
      this.$confirmDelete('Poista työ').then(confirm => {
        if (confirm) {
          const toRemove = this.site.items
            .filter(si => si.taskOrder === drilling.taskOrder && si.task === drilling.task)
            .map(si => si._idx);
          this.site.items = this.site.items.filter(si => !toRemove.includes(si._idx));
          this.site.works = (this.site.works || []).filter(w => w.sortOrder !== drilling.taskOrder);
        }
      });
    },
    async setLocation(geocoding) {
      // Set latitude & longitude

      this.site.geocoding = Object.assign({}, geocoding);
      // Get address by coordinates
      const addressObject = await geocodingApi.getByLatLng(this.site.geocoding.lat, this.site.geocoding.lng);
      this.site.geocoding.address = addressObject?.[0].address;
      this.site.geocoding.result = addressObject?.[0].result;
      //this.$showSuccessNotification('Sijainti tallennettu!');
    },
    openMapDialog() {
      this.$refs.MapDialog.open(this.site);
    },
    log(...args) {
      console.log(args);
    },
    async getDaybooks() {
      if (this.siteId) {
        const daybooks = (await daybookApi.getDaybooksBySiteId(this.siteId))
          .sort((a, b) => a.daybookDate.valueOf() - b.daybookDate.valueOf());
        this.daybooks = daybooks.map(daybook => {
          daybook.entryDuration = this.getEntryDuration(daybook);
          daybook.entryWaitDuration = this.getEntryDuration(daybook, 'Wait');
          daybook.entryWaitDuration2 = this.getEntryDuration(daybook, 'Wait2');
          daybook.entryWaitDuration3 = this.getEntryDuration(daybook, 'Wait3');
          daybook.daybookItems.map(item => (item.files = []));
          return daybook;
        });

        const daybookItemImages = await fileApi.getFilesMultipleObjectIds(
          daybooks.flatMap(daybook => daybook.daybookItems.flatMap(i => i.id)),
          'daybookItem',
        );

        const daybookItemIds = daybookItemImages.map(fileEntity => fileEntity.objectId);
        this.daybooks = daybooks.map(daybook => {
          daybook.daybookItems.map(async item => {
            if (daybookItemIds.includes(item.id)) {
              const files = daybookItemImages.filter(fileEntity => fileEntity.objectId === item.id);

              for (let i = 0; i < files.length; i++) {
                if (files[i]?.contentType?.startsWith('image/')) {
                  try {
                    files[i].thumbnail = await fileApi.getFileUrlLazy(this, files[i]);
                  } catch (e) {
                    console.error(e);
                  }
                }
              }
              item.files = files;
            }
            return item;
          });
          return daybook;
        });

        // Doing this twice to first load all thumbnails and then full source
        this.daybooks = daybooks.map(daybook => {
          daybook.daybookItems.map(async item => {
            if (daybookItemIds.includes(item.id)) {
              const files = daybookItemImages.filter(fileEntity => fileEntity.objectId === item.id);

              for (let i = 0; i < files.length; i++) {
                if (files[i]?.contentType?.startsWith('image/')) {
                  //files[i].thumbnail = await fileApi.getImageUrlLazy(this, files[i]);

                  try {
                    files[i].source = await fileApi.getFileUrl(this, files[i]);
                  } catch (e) {
                    console.error(e);
                  }
                }
              }
              item.files = files;
            }
            return item;
          });
          return daybook;
        });

        // Merkinnän omat kuvat (objectType daybook), näytetään Työmaan tilanne -kohdan alla.
        const daybookImages = (await fileApi.getFilesMultipleObjectIds(daybooks.map(d => d.id), 'daybook'))
          .filter(f => f.contentType?.startsWith('image/'))
          .map(f => ({ ...f, thumbnail: null, source: null }));
        this.daybooks.forEach(daybook => {
          this.$set(daybook, 'files', daybookImages.filter(f => f.objectId === daybook.id));
        });
        for (const file of daybookImages) {
          try {
            file.thumbnail = await fileApi.getFileUrlLazy(this, file);
            file.source = await fileApi.getFileUrl(this, file);
          } catch (e) {
            console.error(e);
          }
        }
      }
    },
    formatDate(date) {
      if (date) return dayjs(date).format('DD.MM.YYYY');
      return;
    },
    async siteWorkerListReport() {
      if (!this.site || !this.site.id) return false;
      this.loadingSiteWorkerListReport = true;
      try {
        const filename = this.$fileNameSanitizer(`${this.site.name}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`);
        downloadjs(await reportApi.getSiteWorkerListReport(this.site.id), filename, 'application/pdf');
      } catch (e) {
        console.error(e);
        this.$showErrorNotification(this.$t('generic_error'));
      }
      this.loadingSiteWorkerListReport = false;
    },
    openDaybookPrintDialog() {
      this.printStartDate = null;
      this.printEndDate = null;
      this.printWorkIds = [];
      this.daybookPrintDialog = true;
    },
    async printDaybooks() {
      this.loadingDaybookPrint = true;
      try {
        const siteId = this.site.id;
        const start = this.printStartDate.replace(/-/g, '');
        const end = this.printEndDate.replace(/-/g, '');
        const siteName = this.site.name.replace(/\s+/g, '-');
        const filename = this.$fileNameSanitizer(`${siteName}-${start}-${end}.pdf`);
        downloadjs(
          await reportApi.getSiteDaybookReport(siteId, this.printStartDate, this.printEndDate, this.printWorkIds),
          filename,
          'application/pdf'
        );
        this.daybookPrintDialog = false;
      } catch (e) {
        console.error(e);
        this.$showErrorNotification(this.$t('generic_error'));
      }
      this.loadingDaybookPrint = false;
    },
    formatIsoDateFi(date) {
      if (!date) return '';
      const [y, m, d] = date.split('-');
      return `${d}.${m}.${y}`;
    },
    async siteDaybook() {
      await this.$router.push({ name: 'tenant_site_new_daybook', params: { siteId: this.site.id } });
    },
    newDispatchNote() {
      if (this.$refs.siteMaterials) {
        this.$refs.siteMaterials.openDispatchNoteDialog();
      }
    },
    async goToDaybook(id) {
      await this.$router.push({ name: 'tenant_site_daybook', params: { siteId: this.site.id, id: id } });
    },
    async openDeleteDaybookDialog(daybook) {
      const confirm = await this.$confirmDelete(
        this.$t('daybook.delete_dialog.description'),
        this.$t('daybook.delete_dialog.title')
      );
      if (confirm === true) {
        await daybookApi.deleteDaybook(daybook.id);
        this.$showSuccessNotification(this.$t('daybook.deleted'));
        await this.getDaybooks();
      }
    },
    getEntryDuration(entry, appendix = '') {
      if (!entry[`entryStart${appendix}`]) return 0;
      if (!entry[`entryEnd${appendix}`]) return this.$t('reports.unfinished');

      let diff;
      let start = entry[`entryStart${appendix}`];
      let end = entry[`entryEnd${appendix}`];

      if (appendix === '' && entry.containsLunch) {
        end = dayjs(end).subtract(0.5, 'hours');
      }

      if (end.isBefore(start)) {
        diff = start.diff(end);
        return -dayjs.duration(diff).asHours();
      } else {
        diff = end.diff(start);
        return dayjs.duration(diff).asHours();
      }
    },
    handleWorkerWorkStartChange(value, worker) {
      const fIndex = this.site.workers.findIndex(w => w.workerId === worker.workerId);
      if (fIndex > -1) {
        this.site.workers[fIndex].workStart = dayjs(value);
      }
    },
    handleWorkerWorkEndChange(value, worker) {
      console.log('hWWEC called');
      console.log(value);
      console.log(worker);
      const fIndex = this.site.workers.findIndex(w => w.workerId === worker.workerId);
      if (fIndex > -1) {
        this.site.workers[fIndex].workEnd = dayjs(value);
      }
    },
    async createSite() {
      if (this.site.name === null || this.site.name.length === 0) {
        return;
      }
      this.loading = true;
      try {
        await siteApi.createSite(this.site);
        this.$showSuccessNotification(this.$t('sites.site_created'));
        await this.$router.push({ name: 'tenant_worksites', params: { siteStatus: 'CREATED' } });
      } catch (e) {
        if (e.response && e.response.status === 409) {
          this.$showErrorNotification(this.$t('sites.site_name_exists_in_project'));
        } else {
          this.$showErrorNotification(this.$t('generic_error'));
        }
      }
      this.loading = false;
    },
    async saveSite() {
      this.loading = true;
      try {
        await siteApi.updateSite(this.id, this.site);
        await this.getSite();
        this.$showSuccessNotification(this.$t('sites.site_saved'));
      } catch (e) {
        if (e.response && e.response.status === 409) {
          this.$showErrorNotification(this.$t('sites.site_name_exists_in_project'));
        } else {
          this.$showErrorNotification(this.$t('generic_error'));
        }
      }
      this.loading = false;
      //await this.$router.go(-1);
    },
    cancel() {
      this.$router.go(-1);
    },
    async getSite() {
      this.siteId = this.id;
      let site = await siteApi.getSite(this.id);
      if (site.geocoding === null) {
        site.geocoding = {
          result: null,
          lat: null,
          lng: null,
          id: null,
          address: '',
        };
      }
      site.items?.forEach((i, idx) => (i._idx = `${idx}_${this.$randomString()}`));

      this.site = site;
      if (!this.site.works || this.site.works.length === 0) {
        this.addWork();
      }

      this.siteWhenMounted = cloneDeep(site);
    },
    async getItems() {
      await loadItemTypes();
      const items = await itemApi.getItemsByTypes(activeTypeIds(type => type.siteAdditionalItem));
      this.items = items;
      this.workMethods = await workMethodsApi.getWorkMethods();
    },
    async getPersons() {
      const personList = (await personApi.getPersonsPaged()).content;
      this.persons = this.$richerUsers(personList);
    },
    async siteMasterCreated(person) {
      await this.getPersons();
      this.site.siteMaster = person;
    },
    async siteGuideCreated(person) {
      await this.getPersons();
      this.site.siteGuide = person;
    },
    async addPerson(personRole) {
      await this.$refs.AddPersonDialog.open(null, personRole);
    },
    async getWorkGroups() {
      this.workGroups = await workGroupApi.getWorkGroups();
    },
    async getEquipments() {
      this.equipments = await equipmentApi.getAllEquipments();
    },
    async getProjects() {
      let projects = await projectApi.getProjects();
      projects = orderBy(projects, ['createdAt'], ['desc']);
      this.projects = projects;
    },
    async getWorkers() {
      this.workers = (await workerApi.getWorkers()).map(worker => {
        worker.workerId = worker.id;
        return worker;
      });
    },
    async getIntroductionsBySite() {
      if (this.id) {
        this.introductions = await introductionApi.getIntroductionsBySite(this.id);
      }
    },
    async onWorkGroupChange(workGroup) {
      const added = [];
      if ((!this.site.workers || (Array.isArray(this.site.workers) && this.site.workers.length === 0)) && workGroup && workGroup.workers) {
        let workStart = this.site.plannedStartDate ? dayjs(this.site.plannedStartDate).clone() : null;
        let workEnd =
          this.site.plannedStartDate && this.site.plannedDuration
            ? dayjs(this.site.plannedStartDate).clone().add(this.site.plannedDuration, 'days')
            : null;
        const workers = [...workGroup.workers].map(w => {
          w.worker = { ...w };
          w.workerId = w.id;
          w.id = void 0;
          w.workStart = dayjs(workStart).clone();
          w.workEnd = dayjs(workEnd).clone();

          return w;
        });
        this.site.workers = workers;
        this.$showSuccessNotification(this.$t('sites.messages.workers_added'));
        added.push('workers');
      }
      if (
        (!this.site.equipments || (Array.isArray(this.site.equipments) && this.site.equipments.length === 0)) &&
        workGroup &&
        workGroup.equipments
      ) {
        const equipments = [...workGroup.equipments];
        this.site.equipments = equipments;
        //this.$showSuccessNotification(this.$t('sites.messages.equipments_added'));
        added.push('equipments');
      }
      if (added.length > 0) {
        this.$showSuccessNotification(
          this.$t('sites.messages.added_x', [added.map(a => this.$tc(`sites.messages.part_${a}`, 1)).join(', ')]),
        );
      }
    },
    async deleteWorker(workerId) {
      const wIndex = this.site.workers.findIndex(w => w.workerId === workerId);
      if (wIndex > -1) {
        this.site.workers.splice(wIndex, 1);
      }
    },
    onTabChange(tab) {
      if (!this.id) return;
      // Reload relevant data when switching tabs
      switch (tab) {
        case 'workers':
          this.getIntroductionsBySite();
          break;
        case 'introduction':
          if (this.$refs.siteIntroduction) {
            if (this.$isTenantAdmin || this.$isTenantManager) {
              this.$refs.siteIntroduction.loadIntroductions();
            } else {
              this.$refs.siteIntroduction.loadMyIntroduction();
            }
          }
          break;
        case 'daybooks':
          this.getDaybooks();
          break;
      }
    },
    async onSelectWorker(worker) {
      if (worker == null) return false;

      let workStart = this.site.plannedStartDate ? dayjs(this.site.plannedStartDate).clone() : null;
      let workEnd =
        this.site.plannedStartDate && this.site.plannedDuration
          ? dayjs(this.site.plannedStartDate).clone().add(this.site.plannedDuration, 'days')
          : null;

      const _worker = {
        ...worker,
        worker: worker,
        workStart,
        workEnd,
        firstname: worker.person.firstname,
        lastname: worker.person.lastname,
      };

      const workers = uniqBy([...this.site.workers, _worker], 'workerId');
      this.site.workers = workers;
      this.$nextTick(() => {
        this.selectedWorker = null;
      });
    },
  },
};
</script>

<style lang='scss' scoped>
.v-toolbar__extension .button_group {
  position: fixed;
  bottom: 16px;
  right: 16px;
}

.grey--bg {
  background-color: var(--v-default-lighten1);
}

.v-tabs-items {
  background-color: transparent !important;
}

.tab-select {
  flex: 1;
  max-width: 300px;
  font-size: 1.1rem;
}

.site-name-field {
  ::v-deep .v-text-field__prefix {
    color: rgba(0, 0, 0, 0.38);
  }
}
</style>
<style lang='scss'>
@import '~vuetify/src/styles/settings/variables';

.tab-select-menu {
  .v-list-item {
    font-size: 1.1rem;

    .v-list-item__title {
      font-size: 1.1rem;
    }
  }
}

@media #{map-get($display-breakpoints,'sm-and-down')} {
  .daybook-list {
    .v-timeline {
      &:before {
        display: none !important;
      }

      &-item__body {
        max-width: 100% !important;

        .v-card {
          &:before,
          &:after {
            top: 0 !important;
            border: 0 !important;
          }
        }
      }

      .v-timeline-item__divider {
        display: none;
      }
    }
  }
}
</style>
